import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { ADMIN_API_URL_SUBPATH } from '@/constants';
import {
  ANSWER,
  axiosInstance,
  CreateEntityResponse,
  INGREDIENT,
  IngredientWithAmount,
  Language,
  PRODUCT,
  Product,
  ProductQuiz,
  QUIZ,
  RECIPE_OVERRIDE,
} from '@/core';
import { RecipeOverride, RecipeOverrideUpdate } from '@/features';

export type CreateProduct = {
  name: Language;
  description?: Language;
  // custom_metadata: any;
  base_price: number | string;
  status: string;
  tax_id?: number; // gets set to default value in db if not provided
  image_id?: number;
  plu_code?: string;
};

export type UpdateProduct = {
  name?: Language;
  description?: Language;
  // custom_metadata?: any;
  base_price?: string;
  status?: string;
  tax_id?: number;
  image_id?: number;
  group_ids?: number[];
  tag_ids?: number[];
  allergen_ids?: number[];
  plu_code?: string;
  takeout_packaging_surcharge?: string;
};

export type ProductIngredientUpdateForm = {
  ingredient_id: number;
  amount: number;
};

export type HandleQuizData = {
  name: Language;
  min_choices: number;
  max_choices: number | null;
  hidden: boolean;
};

export type HandleQuizAnswerData = {
  name: Language;
  kitchen_note: string | null;
  total_price: number | string | null;
  tax_in_percent: number | string | null;
  image_id: number | null;
  ordering: number | null;
  max_amount: number | null;
  allergens: number[] | null;
};

export const useProduct = (config?: {
  onSuccess?: (data: AxiosResponse<CreateEntityResponse>) => void;
  onError?: (error: unknown) => void;
}) => {
  const queryConfig = {
    url: `${ADMIN_API_URL_SUBPATH}/${PRODUCT}`,
  };

  // *** PRODUCT *** //
  const createMutation: UseMutationResult<
    AxiosResponse<CreateEntityResponse>,
    unknown,
    CreateProduct,
    unknown
  > = useMutation({
    mutationKey: ['createProduct'],
    mutationFn: (data: CreateProduct) => axiosInstance.post(queryConfig.url, data),
    onError: (error) => {
      config?.onError?.(error);
    },
    onSuccess: (data) => {
      config?.onSuccess?.(data);
    },
  });

  const useProductQuery = (id: number) =>
    useQuery<AxiosResponse<Product>>({
      queryKey: ['product', id],
      queryFn: () => axiosInstance.get(`${queryConfig.url}/${id}`),
      enabled: !!id,
    });

  const updateMutation = useMutation({
    mutationKey: ['updateProduct'],
    mutationFn: ({ id, data }: { id: number; data: UpdateProduct }) =>
      axiosInstance.put(`${queryConfig.url}/${id}`, data),
  });

  const deleteMutation = useMutation({
    mutationKey: ['deleteProduct'],
    mutationFn: (id: number) => axiosInstance.delete(`${queryConfig.url}/${id}`),
  });

  const activateMutation = useMutation({
    mutationKey: ['activateProduct'],
    mutationFn: (id: number) => axiosInstance.put(`${queryConfig.url}/${id}`, { status: 'active' }),
  });

  const deactivateMutation = useMutation({
    mutationKey: ['deactivateProduct'],
    mutationFn: (id: number) => axiosInstance.put(`${queryConfig.url}/${id}`, { status: 'hidden' }),
  });

  // *** INGREDIENTS *** //
  const useProductIngredients = (productId: number, enabled: boolean = false) =>
    useQuery<AxiosResponse<IngredientWithAmount[]>>({
      queryKey: ['productIngredients', productId, enabled],
      queryFn: () => axiosInstance.get(`${queryConfig.url}/${productId}/${INGREDIENT}`),
      enabled,
    });

  const updateIngredientsMutation = useMutation({
    mutationKey: ['updateProductIngredients'],
    mutationFn: ({ productId, data }: { productId: number; data: ProductIngredientUpdateForm[] }) =>
      axiosInstance.put(`${queryConfig.url}/${productId}/${INGREDIENT}`, data),
  });

  const deleteProductIngredientMutation = useMutation({
    mutationKey: ['deleteProductIngredient'],
    mutationFn: ({ productId, ingredientId }: { productId: number; ingredientId: number }) =>
      axiosInstance.delete(`${queryConfig.url}/${productId}/${INGREDIENT}/${ingredientId}`),
  });

  // *** QUIZZES *** //
  const useProductQuizzes = (productId: number) =>
    useQuery<AxiosResponse<ProductQuiz[]>>({
      queryKey: ['productQuizzes', productId],
      queryFn: () => axiosInstance.get(`${queryConfig.url}/${productId}/${QUIZ}`),
      enabled: !!productId,
    });

  const createProductQuizMutation = useMutation({
    mutationKey: ['createProductQuiz'],
    mutationFn: ({ productId, data }: { productId: number; data: HandleQuizData }) =>
      axiosInstance.post(`${queryConfig.url}/${productId}/${QUIZ}`, data),
  });

  const updateProductQuizMutation = useMutation({
    mutationKey: ['updateProductQuiz'],
    mutationFn: ({ quizId, data }: { quizId: number; data: HandleQuizData }) =>
      axiosInstance.put(`${queryConfig.url}/${QUIZ}/${quizId}`, data),
  });

  const deleteProductQuizMutation = useMutation({
    mutationKey: ['deleteProductQuiz'],
    mutationFn: (quizId: number) => axiosInstance.delete(`${queryConfig.url}/${QUIZ}/${quizId}`),
  });

  // *** QUIZ ANSWERS *** //
  const createProductQuizAnswerMutation = useMutation({
    mutationKey: ['createProductQuizAnswer'],
    mutationFn: ({ quizId, data }: { quizId: number; data: HandleQuizAnswerData }) =>
      axiosInstance.post(`${queryConfig.url}/${QUIZ}/${quizId}/${ANSWER}`, data),
  });

  const updateProductQuizAnswerMutation = useMutation({
    mutationKey: ['updateProductQuizAnswer'],
    mutationFn: ({ answerId, data }: { answerId: number; data: HandleQuizAnswerData }) =>
      axiosInstance.put(`${queryConfig.url}/${QUIZ}/${ANSWER}/${answerId}`, data),
  });

  const deleteProductQuizAnswerMutation = useMutation({
    mutationKey: ['deleteProductQuizAnswer'],
    mutationFn: (answerId: number) =>
      axiosInstance.delete(`${queryConfig.url}/${QUIZ}/${ANSWER}/${answerId}`),
  });

  // *** QUIZ ANSWER ACTIONS *** //
  const useProductRecipeOverrides = (answerId: number) =>
    useQuery<AxiosResponse<RecipeOverride>>({
      queryKey: ['productRecipeOverrides', answerId],
      queryFn: () =>
        axiosInstance.get(`${queryConfig.url}/${QUIZ}/${ANSWER}/${answerId}/${RECIPE_OVERRIDE}`),
      enabled: !!answerId,
    });

  const updateProductRecipeOverrideMutation = useMutation({
    mutationKey: ['updateProductRecipeOverride'],
    mutationFn: ({ answerId, data }: { answerId: number; data: RecipeOverrideUpdate }) =>
      axiosInstance.put(
        `${queryConfig.url}/${QUIZ}/${ANSWER}/${answerId}/${RECIPE_OVERRIDE}`,
        data,
      ),
  });

  return {
    mutations: {
      createMutation,
      updateMutation,
      deleteMutation,
      activateMutation,
      deactivateMutation,
      updateIngredientsMutation,
      deleteProductIngredientMutation,
      createProductQuizMutation,
      updateProductQuizMutation,
      deleteProductQuizMutation,
      createProductQuizAnswerMutation,
      updateProductQuizAnswerMutation,
      deleteProductQuizAnswerMutation,
      updateProductRecipeOverrideMutation,
    },
    queries: {
      useProductQuery,
      useProductIngredients,
      useProductQuizzes,
      useProductRecipeOverrides,
    },
  };
};
